import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
  CardContent,
  useTheme,
  alpha,
  Card
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import FilterBar from '../../common/FilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { DEPARTMENT_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import FeedbackMessage from '../../common/FeedbackMessage';
import { glassCardSx } from '../../common/SaaSTokens';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { motion } from 'framer-motion';

const DepartmentList = () => {
  const { user: currentUser } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const MotionCard = motion(Card);
  const NEON3 = '#34D399';
  const SURFACE_MID  = '#0a1628';

  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  const filterOptions = [
    {
      name: 'description',
      label: 'Description Status',
      type: 'select',
      options: [
        { value: 'has', label: 'Has Description' },
        { value: 'none', label: 'No Description' }
      ]
    }
  ];

  useEffect(() => {
    fetchDepartments({ forceRefresh: Boolean(location.state?.refreshList) });
  }, [location.pathname, location.state?.refreshList]);

  const fetchDepartments = async ({ forceRefresh = false } = {}) => {
    try {
      setLoading(true);
      const response = await api.get('/departments', forceRefresh ? { cache: false } : {});
      setDepartments(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setError('Failed to fetch departments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (department) => {
    setSelectedDepartment(department);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (!selectedDepartment) return;
      setConfirmDeleteLoading(true);
      await api.delete(`/departments/${selectedDepartment.id}`);
      setDepartments(departments.filter(d => d.id !== selectedDepartment.id));
      setSuccess('Department deleted successfully');
    } catch (error) {
      console.error('Error deleting department:', error);
      setError('Failed to delete department. Please try again.');
    } finally {
      setConfirmDeleteLoading(false);
      setDeleteDialogOpen(false);
      setSelectedDepartment(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    const nextActive = newFilters.description === 'has' ? 'has' : newFilters.description === 'none' ? 'none' : 'all';
    setActiveStatFilter(nextActive);
    setFilters(newFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'has', 'none'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.description;
    } else {
      nextFilters.description = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'has' ? 1 :
    activeStatFilter === 'none' ? 2 :
    0;

  const filteredDepartments = departments.filter(department => {
    const matchesSearch = 
      department.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (department.description && department.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (key === 'description') {
        if (value === 'has') return department.description;
        if (value === 'none') return !department.description;
      }
      return true;
    });

    return matchesSearch && matchesFilters;
  });

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    { icon: BusinessIcon, value: departments.length, label: 'Total Departments', color: 'info' },
    { icon: DescriptionIcon, value: departments.filter(d => d.description).length, label: 'With Description', color: 'success' },
    { icon: DescriptionIcon, value: departments.filter(d => !d.description).length, label: 'No Description', color: 'warning' },
  ];

  // Define columns for TableList
  const columns = [
    {
      key: 'name',
      label: 'Name',
      icon: BusinessIcon,
      minWidth: 200,
      render: (department) => (
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {department.name}
        </Typography>
      )
    },
    {
      key: 'description',
      label: 'Description',
      minWidth: 300,
      render: (department) => (
        <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9) }}>
        {department.description || (
            <span style={{ color: theme.palette.text.disabled }}>-</span>
          )}
      </Typography>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 140,
      render: (department) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Department" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); navigate(`/departments/${department.id}`); }}
              size="small"
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Department" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); navigate(`/departments/edit/${department.id}`); }}
              size="small"
              sx={{
                color: theme.palette.warning.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Department" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); handleDelete(department); }}
              size="small"
              sx={{
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (loading) {
    return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading departments list" size={56} variant="primary" />
    </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Header
        title="Department"
        subtitle="Manage organization departments and units"
        icon={BusinessIcon}
        actionButton={
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/departments/add')}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              },
              borderRadius: 2,
              px: 3,
              py: 1.5,
              alignSelf: { xs: 'stretch', sm: 'auto' }
            }}
          >
            Add Department
          </Button>
        }
      />

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_MID, 0.95)})`,
          border: `1px solid ${alpha(NEON3, 0.35)}`,
          boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`,
          color: alpha(NEON3, 0.95),
          mb: 3,
        }}
      />

      {/* Stat Cards */}
      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      {/* Enhanced Filter Bar */}
      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.16 }}
        elevation={0}
        sx={{ ...glassCardSx, mb: 2.5 }}
      >
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterOptions={filterOptions}
            searchPlaceholder="Search by department name or description..."
          />
        </CardContent>
      </MotionCard>

      {/* Departments Table */}
      <ListBulkToolbar
        config={DEPARTMENT_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: DEPARTMENT_BULK_CONFIG.endpoint,
          setItems: setDepartments,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'department(s)',
          refetch: fetchDepartments,
          setError
        })}
      />


      <TableList
        columns={columns}
        data={filteredDepartments}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        {...bulkTableProps(DEPARTMENT_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)}
        loading={loading}
        onRowClick={(department) => navigate(`/departments/${department.id}`)}
        emptyState={{
          icon: <BusinessIcon />,
          title: 'No departments found',
          description: searchTerm || Object.keys(filters).length > 0
            ? "Try adjusting your filters to see more departments."
            : "Get started by adding your first department."
        }}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedDepartment(null);
        }}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        itemType="department"
        itemName={selectedDepartment?.name || ''}
        description="This action cannot be undone and will permanently remove all department data."
        confirmLabel="Delete Department"
        loading={confirmDeleteLoading}
      />
    </Container>
  );
};

export default DepartmentList;